// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Trang tải bản ghi bằng LINK CHIA SẺ.
//
// Đây là màn hình duy nhất của Z-Meet phục vụ một người có thể chưa từng mở
// ứng dụng này: họ nhận một link trong chat công ty và bấm vào. Vì vậy nó không
// đòi đăng nhập, không hỏi gì ngoài passcode, và không giả định người xem biết
// Z-Meet là gì.
//
// # Vì sao mật khẩu là passcode cuộc họp
//
// Một token dùng-một-lần nhúng trong URL bảo mật hơn về lý thuyết và tệ hơn
// trong thực tế: nó biến chính cái link thành bí mật, nên chuyển tiếp link là
// trao luôn quyền, và thu hồi nghĩa là làm hỏng link mọi người đã lưu.
//
// Passcode tách "biết chỗ nào" khỏi "được phép" — đúng cách một cuộc họp đã
// hoạt động, và đúng thứ những người dự họp đã có sẵn trong tay.
//
// # Ba điều màn hình này KHÔNG làm
//
//  1. **Không hiện gì trước khi nhập passcode.** Không tên cuộc họp, không dung
//     lượng, không "bản ghi này đang xử lý". Mỗi mẩu đó là một câu xác nhận
//     rằng id trong URL có thật, và một trang xác nhận id là một oracle để dò.
//  2. **Không nói vì sao bị từ chối.** "Id không tồn tại" và "sai passcode" trả
//     về cùng một câu — cùng luật với đường vào phòng (ADR-0002 bất biến 6).
//  3. **Không giữ lại URL đã ký.** Nó hết hạn sau 15 phút; hiện lại một link
//     chết cho người bấm lần thứ hai còn tệ hơn bắt họ nhập passcode lần nữa.

import { useState } from 'react'
import { DownloadSimple, LockKey } from '@phosphor-icons/react'
import { useI18n } from '../i18n'
import * as api from '../api'

/** bytes đổi số byte thành câu người đọc được. Bản ghi họp tính bằng trăm MB. */
function bytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`
  if (n < 1024 * 1024 * 1024) return `${Math.round(n / (1024 * 1024))} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

/** minutes làm tròn thời lượng lên phút. Giây lẻ không nói thêm điều gì ở đây. */
function minutes(sec: number): number {
  return Math.max(1, Math.round(sec / 60))
}

export function Recording({ id }: { id: string }) {
  const { t } = useI18n()
  const [passcode, setPasscode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [got, setGot] = useState<api.ShareDownload | null>(null)

  const submit = async () => {
    setBusy(true)
    setErr('')
    try {
      const r = await api.shareRecording(id, passcode)
      if (r.ok) {
        setGot(r.data)
        // Mở NGAY thay vì chỉ hiện một cái nút: người dùng vừa bấm "Tải" và
        // chứng minh xong danh tính; bắt họ bấm thêm một nút nữa là thêm một
        // bước không nói thêm điều gì. Nút bên dưới vẫn còn cho trường hợp
        // trình duyệt chặn cửa sổ bật lên.
        location.href = r.data.url
        return
      }
      // ⚠️ Phân nhánh theo CODE, không theo message (ADR-0004 §3).
      switch (r.code) {
        case 'recording.not_ready':
          setErr(t('ui.recshare.not_ready'))
          break
        case 'recording.share_no_passcode':
          setErr(t('ui.recshare.no_passcode'))
          break
        case 'recording.share_throttled':
          setErr(t('ui.recshare.throttled'))
          break
        case 'recording.storage_unconfigured':
          setErr(t('ui.recshare.unavailable'))
          break
        default:
          // Gồm cả "không tìm thấy" lẫn "sai passcode" — server cố ý trả về
          // cùng một mã, và giao diện không được đoán hộ.
          setErr(t('ui.recshare.rejected'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="center">
      <div className="card">
        <h1>{t('ui.recshare.heading')}</h1>
        <p className="sub">{t('ui.recshare.intro')}</p>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (passcode.trim()) void submit()
          }}
        >
          <label htmlFor="rec-pass">{t('ui.recshare.passcode')}</label>
          <input
            id="rec-pass"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          <button className="wide" type="submit" disabled={busy || !passcode.trim()}>
            <LockKey size={16} weight="light" />
            {t('ui.recshare.unlock')}
          </button>
        </form>

        {got && (
          <>
            <div className="msg ok">{t('ui.recshare.starting')}</div>
            {/* Dự phòng cho trình duyệt chặn điều hướng tự động, và cho người
                muốn tải lại mà không nhập passcode lần nữa trong 15 phút. */}
            <a className="dl" href={got.url}>
              <DownloadSimple size={16} weight="light" />
              {t('ui.recshare.download_again')}
            </a>
            <p className="sub">
              {[
                got.title,
                got.size_bytes ? bytes(got.size_bytes) : '',
                got.duration_sec ? t('ui.recshare.minutes', { n: minutes(got.duration_sec) }) : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {/* Nói ra hạn của link. Người dùng hay chép URL đã ký rồi gửi tiếp
                cho người khác, và một link chết sau 15 phút mà không báo trước
                trông như hệ thống hỏng. */}
            <p className="sub">
              {t('ui.recshare.link_expires', { minutes: Math.round(got.expires_in / 60) })}
            </p>
          </>
        )}

        {err && <div className="msg err">{err}</div>}
      </div>
    </main>
  )
}
